import React from 'react';
import { pick } from 'lodash';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Link,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import {
    Clear as ClearIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';

import {
    AutocompleteField,
    BoolField,
    DateField,
    RangeField,
    SliderField
} from '../CommonComponents/Form';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
    setMarketFilters,
    resetMarketFilters,
    setMarketFiltersFromWatchlist,
    selectAllOpenCapacities,
    selectAllOpenJobs,
    selectMarketFilters
} from '../../redux/actions/market';
import { marketFilterLimits } from '../../redux/slices/market';
import { LocationPicker, SectionDivider } from '../CommonComponents';
import type { LocationType } from '../CommonComponents/GoogleMap/LocationPicker';
import MatchModePicker from '../Watchlists/parts/MatchModePicker';
import { routeColor } from '../../utils/tenantRoutes';
import useTenantRoutes from './useTenantRoutes';
import RouteModeChip from './RouteModeChip';
import { updateUserProfile } from '../../api/users';
import { selectUserProfile } from '../../redux/actions/users';
import { fetchAllWatchlists, selectAllWatchlists } from '../../redux/actions/watchlists';
import { fetchAllGroups, selectMyGroups } from '../../redux/actions/groups';
import { selectTenantProfile } from '../../redux/actions/tenants';
import { selectSettableMetadataForOrder } from '../../redux/actions/metadata';
import {
    getAreaReplacement, getWatchlistGeography, getWatchlistUpdateGeography, hasManualRequirements
} from '../../utils/filter-market-entities';
import { getProfileRequirementIds } from '../../utils/metadata';
import type { MetadataOptionType, TenantRoute, WatchlistPrefillType } from '../../types';

type PublisherOptionType = { id: number, name: string };

const PREFILL_FILTER_KEYS = [
    'isJob', 'isCapacity', 'hasListPrice', 'publisherTenantId', 'publisherTenantName',
    'groupId', 'groupName', 'minGrossWeight', 'maxGrossWeight', 'minChargeableWeight', 'maxChargeableWeight',
    'minVolume', 'maxVolume', 'minLoadingMeters', 'maxLoadingMeters', 'minPalletPlaces', 'maxPalletPlaces',
    'minPallets', 'maxPallets'
];

const MarketFilter: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const marketFilters = useSelector(selectMarketFilters());
    const watchlists = useSelector(selectAllWatchlists());
    const groups = useSelector(selectMyGroups());
    const tenantProfile = useSelector(selectTenantProfile());
    const settableMetadata = useSelector(selectSettableMetadataForOrder());
    const allOpenJobs = useSelector(selectAllOpenJobs());
    const allOpenCapacities = useSelector(selectAllOpenCapacities());
    const userProfile = useSelector(selectUserProfile());
    const tenantRoutes = useTenantRoutes();

    const hasMetadataOptions = !!settableMetadata.vehicleTypeOptions;

    const fetchWatchlists = React.useCallback(() => {
        dispatch(fetchAllWatchlists());
    }, [dispatch]);

    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);

    // Publishers visible in the current market entities (anonymous ones have no id)
    const publishers = React.useMemo(() => {
        const byId = new Map<number, PublisherOptionType>();
        [...allOpenJobs, ...allOpenCapacities].forEach((e) => {
            if (e.publisherTenantId && e.publisherTenantName && !byId.has(e.publisherTenantId)) {
                byId.set(e.publisherTenantId, { id: e.publisherTenantId, name: e.publisherTenantName });
            }
        });
        return Array.from(byId.values()).sort((x, y) => x.name.localeCompare(y.name, 'sv'));
    }, [allOpenJobs, allOpenCapacities]);

    // One route, or (route null) all our routes unless `changes` turns insideRoutes off
    const setRoute = (route: TenantRoute | null, changes: object) => dispatch(setMarketFilters({
        ...getAreaReplacement('route'),
        routeId: route?.id ?? null,
        routeName: route?.name ?? '',
        insideRoutes: !route,
        ...changes
    }));

    const setFulfilsProfile = React.useCallback((onlyFulfilling: boolean) => {
        dispatch(setMarketFilters({
            showAllAssignments: !onlyFulfilling,
            fulfilsProfile: onlyFulfilling && tenantProfile
                ? getProfileRequirementIds(tenantProfile, settableMetadata)
                : null
        }));
    }, [dispatch, tenantProfile, settableMetadata]);

    // Opens the watchlist form prefilled with what maps; metadata follows the company profile instead
    const getPrefill = (geography: WatchlistPrefillType): WatchlistPrefillType => {
        const f = marketFilters;
        return {
            ...geography,
            ...pick(f, PREFILL_FILTER_KEYS),
            takesOffer: f.isJob && !f.isDhl,
            hadManualRequirements: hasManualRequirements(f),
            hadAllRoutes: f.insideRoutes
        };
    };
    const saveAsWatchlist = () => {
        navigate('/watchlists/create', {
            state: { prefill: getPrefill(getWatchlistGeography(marketFilters, watchlists)) }
        });
    };
    // Nothing is saved until the form is; saving comes back to the market with the watchlist selected
    const updateSourceWatchlist = () => {
        navigate(`/watchlists/edit/${marketFilters.sourceWatchlistId}`, {
            state: {
                prefill: getPrefill(getWatchlistUpdateGeography(marketFilters, watchlists)),
                returnToMarket: true
            }
        });
    };

    const metadataSelect = (
        label: string,
        options: MetadataOptionType[] | undefined,
        ids: number[] | null,
        onChange: (selectedIds: number[] | null) => void
    ) => (
        <AutocompleteField<MetadataOptionType>
            multiple
            disableCloseOnSelect
            checkboxes
            limitTags={2}
            label={label}
            optionIdKey="id"
            displayOption={(ids ?? []).map((selectedId) => (
                (options ?? []).find((o) => o.id === selectedId) ?? { id: selectedId, name: String(selectedId) }
            ))}
            fetchOptions={() => undefined}
            options={options ?? []}
            getOptionName={(o) => o.name}
            onChange={(selected: MetadataOptionType[]) => onChange(selected.length ? selected.map((o) => o.id) : null)}
            variant="outlined"
            size="small"
        />
    );

    const clearFilters = React.useCallback(() => {
        dispatch(resetMarketFilters());
    }, [dispatch]);

    return (
        <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
            <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                <Grid item>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                        fullWidth
                    >
                        Rensa
                    </Button>
                </Grid>
                <Grid item xs>
                    <Button
                        variant="outlined"
                        startIcon={<NotificationsIcon />}
                        onClick={saveAsWatchlist}
                        fullWidth
                    >
                        {marketFilters.sourceWatchlistId ? 'Spara som ny' : 'Spara som bevakning'}
                    </Button>
                </Grid>
                {!!marketFilters.sourceWatchlistId && (
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={<NotificationsIcon />}
                            onClick={updateSourceWatchlist}
                            fullWidth
                        >
                            {`Uppdatera ${marketFilters.sourceWatchlistName || 'Namnlös bevakning'}`}
                        </Button>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <AutocompleteField<{ id: number | string, name: string | null, publishedCount?: number }>
                        label="Hämta värden från bevakning"
                        optionIdKey="id"
                        displayOption={!marketFilters.watchlistId ? null : {
                            id: marketFilters.watchlistId,
                            name: marketFilters.watchlistName
                        }}
                        fetchOptions={fetchWatchlists}
                        options={watchlists}
                        getOptionName={(o) => o.name || 'Namnlös bevakning'}
                        getOptionDescription={(o) => `${o.publishedCount} träff${o.publishedCount === 1 ? '' : 'ar'}`}
                        onChange={(watchlist) => dispatch(setMarketFiltersFromWatchlist(watchlist))}
                        fetchOnlyOnce
                        variant="outlined"
                        size="small"
                    />
                </Grid>
            </Grid>

            <SectionDivider label="Typ" marginBottom={0} marginTop={2} />
            <Grid container spacing={0} justifyContent="space-between">
                <Grid item>
                    <BoolField
                        name="isJob"
                        label="Uppdrag"
                        value={marketFilters.isJob}
                        onChange={({ isJob }) => dispatch(setMarketFilters({ isJob }))}
                    />
                </Grid>
                <Grid item>
                    <BoolField
                        name="isCapacity"
                        label="Kapacitet"
                        value={marketFilters.isCapacity}
                        onChange={({ isCapacity }) => dispatch(setMarketFilters({ isCapacity }))}
                    />
                </Grid>
                <Grid item>
                    <BoolField
                        name="isDhl"
                        label="Externa uppdrag"
                        value={marketFilters.isDhl}
                        disabled={!marketFilters.isJob}
                        onChange={({ isDhl }) => {
                            dispatch(setMarketFilters({ isDhl }));
                            // The saved setting means "hide external"
                            updateUserProfile({ marketSettingTakesOffer: !isDhl }).catch(() => undefined);
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <BoolField
                        name="showExpired"
                        label="Visa uppdrag där sista dag har passerat"
                        value={!marketFilters.hideExpired}
                        disabled={!marketFilters.isJob}
                        onChange={({ showExpired }) => dispatch(setMarketFilters({ hideExpired: !showExpired }))}
                    />
                </Grid>
            </Grid>

            <SectionDivider label="Område" marginBottom={2} marginTop={2} />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <LocationPicker
                        label="Plats med radie"
                        defaultText={marketFilters.placeName}
                        onChange={(location: LocationType | null) => dispatch(setMarketFilters({
                            ...(location ? getAreaReplacement('place') : {}),
                            placeName: location ? location.city || 'Namnlös' : '',
                            placeLat: location ? location.lat : null,
                            placeLng: location ? location.lng : null
                        }))}
                    />
                </Grid>
                {marketFilters.placeLat !== null && (
                    <>
                        <Grid item xs={12}>
                            <SliderField
                                name="placeRadius"
                                label="Radie [km]"
                                value={marketFilters.placeRadius / 1000}
                                minValue={marketFilterLimits.minLocationRadius}
                                maxValue={marketFilterLimits.maxLocationRadius}
                                step={marketFilterLimits.stepLocationRadius}
                                onChange={(radiusKm) => dispatch(setMarketFilters({ placeRadius: radiusKm * 1000 }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MatchModePicker
                                compact
                                label="Vilka uppdrag ska visas för platsen"
                                value={marketFilters.placeMatchMode}
                                onChange={(placeMatchMode) => dispatch(setMarketFilters({ placeMatchMode }))}
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    {tenantRoutes && tenantRoutes.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {'Ni har inga rutter ännu. '}
                            {userProfile?.isAdmin && (
                                <Link component={RouterLink} to="/routes">Skapa rutter</Link>
                            )}
                        </Typography>
                    )}
                    {!!tenantRoutes?.length && (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                <RouteModeChip
                                    label="Alla våra rutter"
                                    selected={marketFilters.insideRoutes}
                                    mode={marketFilters.routeMatchMode}
                                    onSelect={() => setRoute(null, { insideRoutes: !marketFilters.insideRoutes })}
                                    onModeChange={(routeMatchMode) => setRoute(null, { routeMatchMode })}
                                    chipProps={{ color: 'primary' }}
                                />
                                {tenantRoutes.map((route, index) => {
                                    const selected = marketFilters.routeId === route.id;
                                    const color = routeColor(index);
                                    return (
                                        <RouteModeChip
                                            key={route.id}
                                            label={route.name}
                                            selected={selected}
                                            mode={marketFilters.routeMatchMode}
                                            onSelect={() => setRoute(selected ? null : route, { insideRoutes: false })}
                                            onModeChange={(routeMatchMode) => setRoute(route, { routeMatchMode })}
                                            chipProps={{
                                                sx: {
                                                    border: `1px solid ${color}`,
                                                    color: selected ? '#fff' : color,
                                                    bgcolor: selected ? color : 'transparent',
                                                    '&:hover, &:focus': { bgcolor: selected ? color : 'action.hover' },
                                                    '& .MuiChip-deleteIcon': { color: 'inherit', opacity: 0.8 }
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {marketFilters.routeMatchMode === 'within'
                                    ? 'Visar uppdrag som både hämtas och lämnas i rutten.'
                                    : 'Visar uppdrag som hämtas eller lämnas i rutten.'}
                                {' Håll muspekaren över en rutt för att ändra.'}
                            </Typography>
                        </>
                    )}
                </Grid>
            </Grid>

            <SectionDivider label="Krav" marginBottom={1} />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <RadioGroup
                        value={marketFilters.fulfilsProfile ? 'fulfil' : 'all'}
                        onChange={(e) => setFulfilsProfile(e.target.value === 'fulfil')}
                    >
                        <FormControlLabel value="all" control={<Radio size="small" />} label="Alla uppdrag" />
                        <FormControlLabel
                            value="fulfil"
                            control={<Radio size="small" />}
                            disabled={!tenantProfile || !hasMetadataOptions}
                            label="Bara uppdrag vi uppfyller kraven för"
                        />
                    </RadioGroup>
                </Grid>
                <Grid item xs={12}>
                    {metadataSelect(
                        'Fordonstyper',
                        settableMetadata.vehicleTypeOptions,
                        marketFilters.vehicleTypeIds,
                        (vehicleTypeIds) => dispatch(setMarketFilters({ vehicleTypeIds }))
                    )}
                </Grid>
                <Grid item xs={12}>
                    {metadataSelect(
                        'Utrustning',
                        settableMetadata.equipmentOptions,
                        marketFilters.equipmentIds,
                        (equipmentIds) => dispatch(setMarketFilters({ equipmentIds }))
                    )}
                </Grid>
                <Grid item xs={12}>
                    {metadataSelect(
                        'Licenser och certifikat',
                        settableMetadata.licenceCertificateOptions,
                        marketFilters.licenceCertificateIds,
                        (licenceCertificateIds) => dispatch(setMarketFilters({ licenceCertificateIds }))
                    )}
                </Grid>
                <Grid item xs={12}>
                    {metadataSelect(
                        'Hållbarhet',
                        settableMetadata.sustainabilityOptions,
                        marketFilters.sustainabilityIds,
                        (sustainabilityIds) => dispatch(setMarketFilters({ sustainabilityIds }))
                    )}
                </Grid>
            </Grid>

            <SectionDivider label="Datum" marginBottom={1} />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DateField
                        name="firstPickupDate"
                        label="Tidigaste upphämtning"
                        value={marketFilters.firstPickupDate}
                        onChange={({ firstPickupDate }) => dispatch(setMarketFilters({ firstPickupDate }))}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DateField
                        name="lastDeliveryDate"
                        label="Senaste leverans"
                        value={marketFilters.lastDeliveryDate}
                        onChange={({ lastDeliveryDate }) => dispatch(setMarketFilters({ lastDeliveryDate }))}
                    />
                </Grid>
            </Grid>

            <SectionDivider label="Gods" marginTop={4} marginBottom={1} />

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="grossWeight"
                        label="Totalvikt [kg]"
                        minValue={marketFilterLimits.minGrossWeight}
                        maxValue={marketFilterLimits.maxGrossWeight}
                        lowValue={marketFilters.minGrossWeight || marketFilterLimits.minGrossWeight}
                        highValue={marketFilters.maxGrossWeight || marketFilterLimits.maxGrossWeight}
                        onChange={([minGrossWeight, maxGrossWeight]) => {
                            dispatch(setMarketFilters({ minGrossWeight, maxGrossWeight }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="chargeableWeight"
                        label="FDR-vikt [kg]"
                        minValue={marketFilterLimits.minChargeableWeight}
                        maxValue={marketFilterLimits.maxChargeableWeight}
                        lowValue={marketFilters.minChargeableWeight || marketFilterLimits.minChargeableWeight}
                        highValue={marketFilters.maxChargeableWeight || marketFilterLimits.maxChargeableWeight}
                        onChange={([minChargeableWeight, maxChargeableWeight]) => {
                            dispatch(setMarketFilters({ minChargeableWeight, maxChargeableWeight }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="loadingMeters"
                        label="Flakmeter"
                        minValue={marketFilterLimits.minLoadingMeters}
                        maxValue={marketFilterLimits.maxLoadingMeters}
                        lowValue={marketFilters.minLoadingMeters || marketFilterLimits.minLoadingMeters}
                        highValue={marketFilters.maxLoadingMeters || marketFilterLimits.maxLoadingMeters}
                        onChange={([minLoadingMeters, maxLoadingMeters]) => {
                            dispatch(setMarketFilters({ minLoadingMeters, maxLoadingMeters }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="volume"
                        label="Volym [m³]"
                        minValue={marketFilterLimits.minVolume}
                        maxValue={marketFilterLimits.maxVolume}
                        lowValue={marketFilters.minVolume || marketFilterLimits.minVolume}
                        highValue={marketFilters.maxVolume || marketFilterLimits.maxVolume}
                        onChange={([minVolume, maxVolume]) => {
                            dispatch(setMarketFilters({ minVolume, maxVolume }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="palletPlaces"
                        label="Pallplatser"
                        minValue={marketFilterLimits.minPalletPlaces}
                        maxValue={marketFilterLimits.maxPalletPlaces}
                        lowValue={marketFilters.minPalletPlaces || marketFilterLimits.minPalletPlaces}
                        highValue={marketFilters.maxPalletPlaces || marketFilterLimits.maxPalletPlaces}
                        onChange={([minPalletPlaces, maxPalletPlaces]) => {
                            dispatch(setMarketFilters({ minPalletPlaces, maxPalletPlaces }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RangeField
                        compact
                        name="pallets"
                        label="Pallar"
                        minValue={marketFilterLimits.minPallets}
                        maxValue={marketFilterLimits.maxPallets}
                        lowValue={marketFilters.minPallets || marketFilterLimits.minPallets}
                        highValue={marketFilters.maxPallets || marketFilterLimits.maxPallets}
                        onChange={([minPallets, maxPallets]) => {
                            dispatch(setMarketFilters({ minPallets, maxPallets }));
                        }}
                    />
                </Grid>
            </Grid>
            <SectionDivider label="Publicering" marginTop={2} marginBottom={1} />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                        label="Endast i grupp"
                        optionIdKey="id"
                        displayOption={!marketFilters.groupId ? null : {
                            id: marketFilters.groupId,
                            name: marketFilters.groupName
                        }}
                        fetchOptions={fetchGroups}
                        options={groups.filter(
                            (o: { isOwner: boolean; isAccepted: boolean; }) => (o.isOwner || o.isAccepted)
                        )}
                        getOptionName={(o) => o.name}
                        getOptionDescription={(o) => o.ownerTenantName || ''}
                        onChange={(group) => dispatch(setMarketFilters({
                            groupId: group ? group.id : null,
                            groupName: group ? group.name : null
                        }))}
                        fetchOnlyOnce
                    />
                </Grid>

                <Grid item xs={12}>
                    <AutocompleteField<PublisherOptionType>
                        label="Endast från avsändare"
                        helperText="Bland de avsändare som syns i uppdragen."
                        optionIdKey="id"
                        displayOption={!marketFilters.publisherTenantId ? null : {
                            id: marketFilters.publisherTenantId,
                            name: marketFilters.publisherTenantName
                        }}
                        fetchOptions={() => undefined}
                        options={publishers}
                        getOptionName={(o) => o.name}
                        onChange={(publisher: PublisherOptionType | null) => dispatch(setMarketFilters({
                            publisherTenantId: publisher ? publisher.id : null,
                            publisherTenantName: publisher ? publisher.name : ''
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <BoolField
                        name="hasListPrice"
                        label="Endast uppdrag med riktpris"
                        value={marketFilters.hasListPrice}
                        onChange={({ hasListPrice }) => dispatch(setMarketFilters({ hasListPrice }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <BoolField
                        name="hasOffer"
                        label="Endast uppdrag där ni anmält intresse"
                        value={marketFilters.hasOffer}
                        onChange={({ hasOffer }) => dispatch(setMarketFilters({ hasOffer }))}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MarketFilter;
